import { FolderOpen, TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/ui/components/ui/badge';
import { Button } from '@/ui/components/ui/button';
import { Card, CardContent } from '@/ui/components/ui/card';
import { StatusPlaceholder } from '@/ui/components/ui/status-placeholder';
import { useAdminAuth } from '../../context/AdminAuthContext';
import type { ProjectObject } from '../../hooks/use-admin';
import { formatEnum } from '../../utils/format-enum';

interface ProjectListProps {
	projects: ProjectObject[];
	loading: boolean;
	error: boolean;
	onCreateClick: () => void;
}

export function ProjectList({
	projects,
	loading,
	error,
	onCreateClick,
}: ProjectListProps) {
	const navigate = useNavigate();

	if (error) {
		return (
			<Card>
				<CardContent>
					<StatusPlaceholder
						icon={TriangleAlert}
						title='Failed to load projects'
						description='Something went wrong. Please try again later.'
						variant='error'
					/>
				</CardContent>
			</Card>
		);
	}

	if (loading && projects.length === 0) {
		return (
			<div className='flex flex-col gap-2'>
				{[1, 2, 3].map((i) => (
					<Card key={i} className='h-20 animate-pulse' />
				))}
			</div>
		);
	}

	if (!loading && projects.length === 0) {
		return (
			<Card>
				<CardContent>
					<StatusPlaceholder
						icon={FolderOpen}
						title='No projects yet'
						description='Create your first project to start collecting feedback.'
						action={
							<Button onClick={onCreateClick}>
								Create project
							</Button>
						}
					/>
				</CardContent>
			</Card>
		);
	}

	return (
		<div className='flex flex-col gap-2'>
			{projects.map((project) => (
				<ProjectCard
					key={project.id}
					project={project}
					onClick={() => navigate(`/admin/projects/${project.id}`)}
				/>
			))}
		</div>
	);
}

function ProjectCard({
	project,
	onClick,
}: {
	project: ProjectObject;
	onClick: () => void;
}) {
	const { user } = useAdminAuth();
	const isModerator = user?.role.toUpperCase() === 'MODERATOR';
	const displayRole =
		isModerator && !project.canEdit
			? 'Viewer'
			: user
				? formatEnum(user.role)
				: null;

	return (
		<Card
			className='cursor-pointer hover:shadow-ionos-card-voted transition-shadow py-4'
			onClick={onClick}
			onKeyUp={(e) => e.key === 'Enter' && onClick()}
			role='button'
			tabIndex={0}
		>
			<CardContent className='py-0'>
				<div className='flex items-start justify-between gap-4'>
					<div className='flex-1 min-w-0'>
						<div className='flex items-center gap-2 mb-1 flex-wrap'>
							<span className='font-semibold text-foreground text-base truncate'>
								{project.name}
							</span>
							{displayRole && (
								<Badge variant='secondary'>{displayRole}</Badge>
							)}
							<StatusBadge status={project.status} />
						</div>
						{project.description && (
							<p className='text-sm text-muted-foreground truncate'>
								{project.description}
							</p>
						)}
						<p className='text-xs text-muted-foreground/60 mt-1'>
							Tenant: {project.tenantName ?? project.tenantId}
						</p>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function StatusBadge({ status }: { status: string }) {
	const variant = status === 'active' ? 'default' : 'secondary';
	return <Badge variant={variant}>{formatEnum(status)}</Badge>;
}
